import { oc } from "@orpc/contract";
import * as z from "zod";

const Id = z.string().min(1).max(200);

export const DrokAssignmentRowSchema = z.object({
  id: Id,
  userId: Id,
  name: z.string(),
  email: z.string(),
  state: z.enum(["active", "unassigned"]),
  /** The version the copy is on now, or null before the first successful push. */
  version: z.number().int().nullable(),
  versionId: z.string().nullable(),
  copyBotId: z.string().nullable(),
  assignedAt: z.string(),
  unassignedAt: z.string().nullable(),
  lastPushedAt: z.string().nullable(),
  lastPushError: z.string().nullable(),
});
export type DrokAssignmentRow = z.infer<typeof DrokAssignmentRowSchema>;

export const DrokPushWarningSchema = z.object({
  code: z.literal("skill_renamed"),
  message: z.string(),
});

export const DrokPushOutcomeSchema = z.object({
  assignmentId: Id,
  userId: Id,
  outcome: z.enum(["created", "updated", "unchanged", "restored"]),
  version: z.number().int(),
  versionId: Id,
  warnings: z.array(DrokPushWarningSchema),
});
export type DrokPushOutcome = z.infer<typeof DrokPushOutcomeSchema>;

export const DrokAssignResultSchema = z.object({
  userId: Id,
  status: z.enum(["pushed", "failed", "not_signed_in", "not_found", "blocked"]),
  assignmentId: Id.nullable(),
  push: DrokPushOutcomeSchema.nullable(),
  message: z.string().nullable(),
});
export type DrokAssignResult = z.infer<typeof DrokAssignResultSchema>;

export const DrokPushAllResultSchema = z.object({
  assignmentId: Id,
  userId: Id,
  ok: z.boolean(),
  push: DrokPushOutcomeSchema.nullable(),
  error: z.string().nullable(),
});
export type DrokPushAllResult = z.infer<typeof DrokPushAllResultSchema>;

export const DrokUserRowSchema = z.object({
  id: Id,
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  isIt: z.boolean(),
  blocked: z.boolean(),
  hasPersonalSpace: z.boolean(),
});

/** IT-only. None of these is in the employee access map, so they stay default-deny. */
export const drokAssignmentContract = {
  list: oc.input(z.object({ templateId: Id })).output(z.array(DrokAssignmentRowSchema)),
  assign: oc
    .input(z.object({ templateId: Id, userIds: z.array(Id).min(1).max(200) }))
    .output(z.array(DrokAssignResultSchema)),
  push: oc
    .input(z.object({ assignmentId: Id, versionId: Id.optional() }))
    .output(DrokPushOutcomeSchema),
  pushAll: oc
    .input(z.object({ templateId: Id, versionId: Id.optional() }))
    .output(z.array(DrokPushAllResultSchema)),
  unassign: oc.input(z.object({ assignmentId: Id })).output(z.object({ ok: z.literal(true) })),
};

export const drokUserContract = {
  list: oc
    .input(
      z
        .object({
          search: z.string().trim().max(200).optional(),
          cursor: Id.optional(),
          limit: z.number().int().min(1).max(100).default(50),
        })
        .default({ limit: 50 }),
    )
    .output(z.object({ users: z.array(DrokUserRowSchema), nextCursor: z.string().nullable() })),
};
